import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { LanguageContext } from "../App";

const COOKIE_KEY = "aurora_cookie_consent";

const COPY = {
  it: {
    title: "Utilizziamo i cookie",
    text: "Questo sito usa cookie tecnici (essenziali) e, con il tuo consenso, cookie analitici per migliorare la tua esperienza.",
    privacy: "Privacy Policy",
    acceptAll: "Accetta tutti",
    essential: "Solo essenziali",
  },
  en: {
    title: "We use cookies",
    text: "This site uses technical (essential) cookies and, with your consent, analytical cookies to improve your experience.",
    privacy: "Privacy Policy",
    acceptAll: "Accept all",
    essential: "Essential only",
  },
  de: {
    title: "Wir verwenden Cookies",
    text: "Diese Website verwendet technische Cookies und mit Ihrer Zustimmung Analyse-Cookies, um Ihr Erlebnis zu verbessern.",
    privacy: "Datenschutzerklärung",
    acceptAll: "Alle akzeptieren",
    essential: "Nur wesentliche",
  },
  nl: {
    title: "Wij gebruiken cookies",
    text: "Deze website gebruikt technische (essentiële) cookies en, met jouw toestemming, analytische cookies om je ervaring te verbeteren.",
    privacy: "Privacybeleid",
    acceptAll: "Alles accepteren",
    essential: "Alleen essentieel",
  },
};

export default function CookieBanner() {
  const { lang } = useContext(LanguageContext);
  const [visible, setVisible] = useState(false);
  const c = COPY[lang];

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (type) => {
    localStorage.setItem(COOKIE_KEY, type);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[100] bg-[#FDFBF7] border-t border-[#DDD8CE] shadow-lg"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex-1 min-w-0">
              <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.15em] text-[#2A2726] mb-1.5">
                {c.title}
              </p>
              <p className="font-['Manrope',sans-serif] text-xs text-[#5C5754] leading-relaxed">
                {c.text}{" "}
                <Link
                  to="/privacy"
                  data-testid="cookie-privacy-link"
                  className="text-[#7B907F] underline underline-offset-2 hover:opacity-70 transition-opacity"
                  onClick={() => accept("essential")}
                >
                  {c.privacy}
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                data-testid="cookie-essential-btn"
                onClick={() => accept("essential")}
                className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.15em] text-[#2A2726] border border-[#2A2726] px-5 py-2.5 hover:bg-[#F5F1EA] transition-colors duration-200 whitespace-nowrap"
              >
                {c.essential}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => accept("all")}
                className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.15em] bg-[#7B907F] text-white px-5 py-2.5 hover:bg-[#687A6C] transition-colors duration-200 whitespace-nowrap"
              >
                {c.acceptAll}
              </button>
              <button
                data-testid="cookie-close-btn"
                onClick={() => accept("essential")}
                className="text-[#8A847F] hover:text-[#2A2726] transition-colors p-1"
                aria-label="Chiudi"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
